import { HFlow, Icon, PagedTable, Text, VFlow } from 'bold-ui'
import { DateTime } from 'components/date'
import { usePecField } from 'components/form/final-form/useField'
import { TableBox } from 'components/table/TableBox'
import { usePagedTableProps } from 'components/table/usePagedTableProps'
import { useAtendimentoAtestadoQuery, useLoadAtestadosQuery } from 'graphql/hooks.generated'
import { AtendimentoProfissional, Atestado, AtestadoQueryInput } from 'graphql/types.generated'
import moment from 'moment'
import React, { useState } from 'react'
import { MetaPath } from 'util/metaPath'

import AtestadoModel from '../components/AtestadoModel'
import AtestadoTableButtons from './AtestadoTableButtons'

export interface AtestadoTableProps {
  name: MetaPath<AtestadoModel>
  atendimentoId: ID
  prontuarioId: ID
  remove(index: number): void
}

export interface RegistroLista {
  id: ID
  recente: boolean
  atendimentoProfissional: AtendimentoProfissional
}

export default function AtestadosTable(props: AtestadoTableProps) {
  const { name, atendimentoId, prontuarioId, remove } = props
  const { input } = usePecField({ name })

  const {
    data: { atendimento },
  } = useAtendimentoAtestadoQuery({
    fetchPolicy: 'cache-first',
    variables: { atendimentoId: atendimentoId },
  })

  const {
    data: { atestados },
  } = useLoadAtestadosQuery({
    fetchPolicy: 'cache-and-network',
    variables: { input: { prontuarioId: prontuarioId } },
  })

  const renderData = (row: RegistroLista) => {
    return (
      <Text>
        <DateTime value={moment(row?.atendimentoProfissional?.iniciadoEm)} format={`DD/MM/YYYY`} />
      </Text>
    )
  }

  const renderProfissional = (row: RegistroLista) => {
    return (
      <Text>
        <VFlow vSpacing={0}>
          <Text component='b'>{row?.atendimentoProfissional?.lotacao.profissional.nome}</Text>
          {row?.atendimentoProfissional?.lotacao.cbo.nome}
        </VFlow>
      </Text>
    )
  }

  const renderUnidadeSaude = (row: RegistroLista) => {
    return <Text>{row?.atendimentoProfissional?.lotacao.unidadeSaude.nome}</Text>
  }

  const renderStatus = (row: RegistroLista) => {
    return (
      row.recente && (
        <Text color='primary'>
          <HFlow alignItems='center' hSpacing={0.5}>
            <Icon size={1} icon='clockOutline' />
            Adicionado agora
          </HFlow>
        </Text>
      )
    )
  }

  const renderButtons = (row: RegistroLista) => {
    return (
      <AtestadoTableButtons
        registro={row}
        cidadao={atendimento?.cidadao}
        atestadoRecente={row.recente && atestadosRecentes[row.id]}
        remove={remove}
      />
    )
  }

  const atestadosAntigos = (atestados?.content ?? []).sort(
    (a, b) => b.atendimentoProfissional.iniciadoEm - a.atendimentoProfissional.iniciadoEm
  )
  const atestadosRecentes = input.value as AtestadoModel[]

  const atestadosExibir: RegistroLista[] = [
    ...(Object.keys(atestadosRecentes)
      .map((it) => convertRecentes(it, atendimento))
      .reverse() ?? []),
    ...(atestadosAntigos.map(convertAtestadoBanco) ?? []),
  ]

  const [tableState, setTableState] = useState<AtestadoQueryInput>({
    prontuarioId: prontuarioId,
    pageParams: { page: 0, size: 5 },
  })

  const { page: currentPage, size: itensPerPage } = tableState.pageParams
  const totalElements = atestadosExibir?.length ?? 0
  const totalPages = Math.ceil(totalElements / itensPerPage)

  const content = atestadosExibir?.slice(currentPage * itensPerPage, (currentPage + 1) * itensPerPage) ?? []
  const atestadosPage = {
    content: content,
    pageInfo: {
      number: currentPage,
      size: itensPerPage,
      totalPages: totalPages,
      totalElements: atestadosExibir?.length || 0,
      first: currentPage === 0,
      last: currentPage === totalPages - 1,
      numberOfElements: content.length,
    },
    paginationOptions: [5, 10, 30],
  }

  const tableProps = usePagedTableProps({
    loading: false,
    onChange: setTableState,
    result: atestadosPage,
  })

  return (
    <TableBox>
      <PagedTable<RegistroLista>
        {...tableProps}
        columns={[
          { name: 'data', header: 'Data', render: renderData },
          { name: 'profissional', header: 'Profissional', render: renderProfissional, style: { padding: '0.5rem' } },
          { name: 'unidadeSaude', header: 'Unidade de saúde', render: renderUnidadeSaude },
          { name: 'status', render: renderStatus },
          { name: 'botoes', render: renderButtons },
        ]}
      />
    </TableBox>
  )
}

const convertAtestadoBanco = (atestado: Atestado) => {
  return {
    id: atestado.id,
    atendimentoProfissional: atestado.atendimentoProfissional,
    recente: false,
  } as RegistroLista
}
const convertRecentes = (id: string, atendimento: any) => {
  return {
    id: id,
    atendimentoProfissional: atendimento?.atendimentoProfissional,
    recente: true,
  } as RegistroLista
}
